import { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import CardBody from 'components/card/CardBody';
import CardFooter from 'components/card/CardFooter';
import messages from 'messages.js';
import { CardHeader, CardHeaderTitle } from 'components/card/card-header';
import { StoreContext } from 'index';
import FormEditHeaderButtons from 'containers/partials/buttons/FormEditHeaderButtons';
import FormEditFooterButtons from 'containers/partials/buttons/FormEditFooterButtons';
import PersonForm from 'containers/pages/persons/PersonForm';
import useEdit from 'hooks/utils/useEdit';
import {
  ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_PERSONS_PERSON_GLOBAL_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT
} from 'routes/RouteList';
import {
  MUTATE_RESET_LOGIN,
  QUERY_GET_USER_BY_ID,
  QUERY_GET_USERS_BY_ENTITY_ID
} from 'services/aws/user-query';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import PersonFormDetails from 'containers/pages/persons/PersonFormDetails';
import { useNotificationQueue } from 'components/notification';
import GenerateMagicURLModal from 'containers/partials/modals/GenerateMagicURLModal';

const PersonCard = ({ person, onCancel, isAdd = false }) => {
  const ability = useAbility(AbilityContext);
  const intl = useIntl();
  const notification = useNotificationQueue();
  const {
    authStore: { user },
    uiState
  } = useContext(StoreContext);
  const { push } = useHistory();
  const { entityId, groupId } = useParams();
  const [openGenerateMagicURLModal, setOpenGenerateMagicURLModal] =
    useState(false);

  const [edit, setEdit] = useEdit(
    !!useRouteMatch([
      ROUTE_PERSONS_ENTITY_PERSON_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT,
      ROUTE_SESSIONS_ENTITY_SESSION_PERSON_GLOBAL_EDIT,
      ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_PERSONS_PERSON_GLOBAL_EDIT
    ]) || isAdd,
    !isAdd
  );

  const onPersonAdded = personId => {
    push(
      generatePath(ROUTE_PERSONS_ENTITY_PERSON_GLOBAL, {
        entityId,
        personId: personId
      })
    );
  };

  const onPersonUpdated = () => {
    setEdit(false);
  };

  const onClickPersonStatusHandler = person => {
    // @ts-ignore
    uiState.showModal({
      title: intl.formatMessage(messages.modalConfirmResetPasswordTitle),
      message: messages.modalConfirmResetPasswordMessage,
      okLabel: intl.formatMessage(
        messages.modalConfirmResetPasswordAcceptButton
      ),
      dismissLabel: intl.formatMessage(messages.modalConfirmRemoveCancelButton),
      mutationData: {
        mutation: MUTATE_RESET_LOGIN,
        variables: {
          personId: person.id
        },
        refetchQueries: [
          {
            query: QUERY_GET_USERS_BY_ENTITY_ID,
            variables: { id: entityId }
          },
          {
            query: QUERY_GET_USER_BY_ID,
            variables: {
              id: person.id
            }
          }
        ],
        update: (cache, { data: { updatePersonEntityRoles } }) => {
          if (updatePersonEntityRoles) {
            // refetch();
            notification.add(`emailReset_${person.id}`, {
              message: intl.formatMessage(messages.messageUserEmailReset)
            });
          }
        }
      }
    });
  };

  return (
    <>
      <PersonForm
        entityId={entityId}
        groupId={groupId}
        person={{ ...person }}
        onAdded={personId => onPersonAdded(personId)}
        onComplete={personId => onPersonUpdated(personId)}
        extraClassNames={'c-card c-card--secondary'}
      >
        {({ submitForm, resetForm }) => (
          <>
            <CardHeader secondary>
              <CardHeaderTitle>
                <FormattedMessage {...messages.cardTitleSporter} />
              </CardHeaderTitle>
              {!user.isPerson() && ability.can('manage', 'Persons') && (
                <FormEditHeaderButtons
                  edit={edit}
                  setEdit={edit => setEdit(edit)}
                  type={'button'}
                  onSubmit={submitForm}
                  onCancel={() => {
                    resetForm();
                    onCancel && onCancel();
                  }}
                />
              )}
            </CardHeader>
            <CardBody secondary>
              <PersonFormDetails
                entityId={entityId}
                person={person}
                edit={edit}
                onClickPersonStatus={onClickPersonStatusHandler}
              />
            </CardBody>
            {edit && (
              <CardFooter secondary>
                <FormEditFooterButtons
                  edit={edit}
                  setEdit={edit => setEdit(edit)}
                  onCancel={() => {
                    resetForm();
                    onCancel && onCancel();
                  }}
                  onSubmit={submitForm}
                  type={'button'}
                />
              </CardFooter>
            )}
          </>
        )}
      </PersonForm>

      {openGenerateMagicURLModal && (
        <GenerateMagicURLModal
          entityId={entityId}
          person={person}
          onClose={() => setOpenGenerateMagicURLModal(false)}
        />
      )}
    </>
  );
};
export default PersonCard;
